import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/EditOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { ClarificationQuestion, LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import { followUpOf } from '../../stores/slices/landingAgentSlice';
import { stromaFocusRing } from './askPresentation';

/** A question in the conversation: words, with no frame of their own. */
const QUESTION = { flex: 1, minWidth: 0, fontWeight: 500, lineHeight: 1.45, maxWidth: '72ch', whiteSpace: 'pre-line', overflowWrap: 'anywhere' } as const;

/** One option, as a line of words. */
const optionRow = (selected: boolean) => ({
  display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1.25, width: '100%', px: 1, py: 0.75, borderRadius: '8px', textAlign: 'left',
  bgcolor: selected ? 'var(--accent-soft)' : 'transparent',
  '&:hover': { bgcolor: selected ? 'var(--accent-tint)' : 'action.hover' },
  '&.Mui-focusVisible': { outline: '2px solid var(--accent)', outlineOffset: '-2px' },
});

type QuestionState = {
  selectedIndex: number | null; // index into options; options.length === "Something else"
  customText: string;
};

function formatAnswer(q: { options?: string[] }, st: QuestionState): string {
  if (st.selectedIndex == null) return '';
  const opts = q.options || [];
  if (st.selectedIndex < opts.length) return opts[st.selectedIndex];
  return st.customText.trim();
}

/** The router's clarifier (#760): its one question's options each name a route, and picking one re-asks the
 *  question under that route. "Something else" is a new question in the asker's own words, as for any clarifier. */
function routeOf(questions: ClarificationQuestion[], idx: number | null): string | null {
  const routes = questions.length === 1 ? questions[0].routes : undefined;
  return routes && idx != null && idx < routes.length ? routes[idx] : null;
}

/** An ask about an earlier answer (#1807): each option re-asks the question with the earlier answer it names. */
function earlierOf(questions: ClarificationQuestion[], idx: number | null) {
  const earliers = questions.length === 1 ? questions[0].earliers : undefined;
  return earliers && idx != null && idx < earliers.length ? earliers[idx] : null;
}

/** A metric named outside the pick (#767): each option re-asks the question in a perspective, for this question
 *  or as the thread's pick — the asker's choice, never made for them. */
function scopeOf(questions: ClarificationQuestion[], idx: number | null) {
  const scopes = questions.length === 1 ? questions[0].scopes : undefined;
  return scopes && idx != null && idx < scopes.length ? scopes[idx] : null;
}

/**
 * The reading question (#1818, design-1818 §3.4): which reading of a meaning no person has settled this answer uses.
 * Its options are the server's, rendered for this reader on its block's items; a click re-asks the turn's own request
 * with `default_change` (`changeDefault`, F3's one contract). No typed reply and no Skip: words are never a reading.
 * A block the reader cannot read (the label-free item) or none shows the prompt alone.
 */
function ReadingQuestionCard({ turn, question }: { turn?: LandingAgentTurn; question: ClarificationQuestion }) {
  const changeDefault = useVoyagerStore((s) => s.changeDefault);
  const resolveWait = useVoyagerStore((s) => s.resolveWait);
  const resetLandingAgent = useVoyagerStore((s) => s.resetLandingAgent);
  const landingAgentStatus = useVoyagerStore((s) => s.landingAgentStatus);
  const [submitted, setSubmitted] = React.useState(false);
  const block = question.defaults;
  const can = !!turn?.askedRequest && block?.changeable === true;
  const items = (block?.items || []).filter((i) => i && i.kind === 'reading' && (i.options || []).length > 0);
  const active = !submitted && (!turn || landingAgentStatus === 'clarifying');
  const choose = (id: string, option: number) => {
    if (!turn || !active) return;
    if (changeDefault(turn.id, id, option)) setSubmitted(true);
  };
  return (
    <Box data-testid="reading-question">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: items.length ? 1.25 : 0, gap: 1 }}>
        <Typography variant="body1" sx={QUESTION}>{question.prompt}</Typography>
        {active && (
          <IconButton size="small" onClick={() => { if (turn) resolveWait(turn.id); resetLandingAgent(); }} aria-label="Cancel">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {items.map((item) => (
        <Box key={item.id} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', '&:not(:last-child)': { mb: 1 } }}>
          {item.options.map((o) => (
            <Button key={o.option} variant="outlined" color="inherit" size="small" aria-pressed={!!o.current}
                    data-testid={`reading-option-${item.id}-${o.option}`}
                    disabled={!can || !active} onClick={() => choose(item.id, o.option)}
                    sx={{ textTransform: 'none', textAlign: 'left', borderColor: 'var(--line)', ...(o.current ? { '&&': { bgcolor: 'var(--accent-tint)' } } : {}) }}>
              {o.label}
            </Button>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function ClarificationCard({ turn }: { turn?: LandingAgentTurn } = {}) {
  const storeClar = useVoyagerStore((s) => s.landingAgentClarification);
  const clar = turn ? turn.clarification : storeClar;
  const reading = (clar?.questions || []).find((q) => q.id === 'reading');
  if (reading) return <ReadingQuestionCard turn={turn} question={reading} />;
  return <Clarifier turn={turn} />;
}

function Clarifier(
  { turn }: { turn?: LandingAgentTurn } = {},
) {
  const storeClar = useVoyagerStore((s) => s.landingAgentClarification);
  const clar = turn ? turn.clarification : storeClar;
  const resetLandingAgent = useVoyagerStore((s) => s.resetLandingAgent);
  // a Cancel ends the wait on the asker (#1503): recorded on the turn before the screen is cleared
  const resolveWait = useVoyagerStore((s) => s.resolveWait);
  const sendLandingAgentQuery = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const reaskInPerspective = useVoyagerStore((s) => s.reaskInPerspective);
  const landingAgentStatus = useVoyagerStore((s) => s.landingAgentStatus);

  const questions = clar?.questions || [];

  const [states, setStates] = React.useState<Record<string, QuestionState>>(() => {
    const init: Record<string, QuestionState> = {};
    for (const q of questions) {
      const opts = q.options || [];
      init[q.id] = { selectedIndex: opts.length > 0 ? 0 : null, customText: '' };
    }
    return init;
  });
  const [submitted, setSubmitted] = React.useState(false);
  const [pageIdx, setPageIdx] = React.useState(0);
  const customInputRef = React.useRef<HTMLInputElement | null>(null);
  const cardRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setPageIdx(0);
  }, [clar?.sub_task_id]);

  const currentQ = questions[pageIdx];
  const focusedQid = currentQ?.id || null;
  const isLastPage = pageIdx === questions.length - 1;
  const currentAnswered = currentQ
    ? formatAnswer(currentQ, states[currentQ.id] || { selectedIndex: null, customText: '' }).length > 0
    : false;

  const isActive = !!clar && !submitted && landingAgentStatus === 'clarifying';
  const allAnswered = questions.every((q) => formatAnswer(q, states[q.id] || { selectedIndex: null, customText: '' }).length > 0);

  const setQuestion = (qid: string, patch: Partial<QuestionState>) => {
    setStates((prev) => ({ ...prev, [qid]: { ...(prev[qid] || { selectedIndex: null, customText: '' }), ...patch } }));
  };

  const pickOption = (qid: string, idx: number, commit: boolean = false) => {
    setQuestion(qid, { selectedIndex: idx });
    const q = questions.find((x) => x.id === qid);
    const opts = q?.options || [];
    const isCustom = idx === opts.length;
    if (isCustom) {
      // "Something else" — focus the inline input next tick, don't auto-advance
      setTimeout(() => customInputRef.current?.focus(), 0);
      return;
    }
    if (!commit) return;
    if (qid !== currentQ?.id) return;
    const route = routeOf(questions, idx);
    if (route && turn?.askedRequest) {
      setSubmitted(true);
      sendLandingAgentQuery(turn.askedRequest.query, { reask: turn.askedRequest, route });
      return;
    }
    const earlier = earlierOf(questions, idx);
    if (earlier && turn?.askedRequest) {
      setSubmitted(true);
      sendLandingAgentQuery(turn.askedRequest.query, { reask: turn.askedRequest, earlier });
      return;
    }
    const scope = scopeOf(questions, idx);
    if (scope && turn?.askedRequest) {
      setSubmitted(true);
      reaskInPerspective(turn, scope.perspective_id, scope.for);
      return;
    }
    if (isLastPage) {
      const projectedAnswer = opts[idx] || '';
      const projectedAll = questions.every((qq) => {
        if (qq.id === qid) return projectedAnswer.length > 0;
        return formatAnswer(qq, states[qq.id] || { selectedIndex: null, customText: '' }).length > 0;
      });
      if (!projectedAll) return;
      const parts = questions.map((qq) => {
        const a = qq.id === qid ? projectedAnswer : formatAnswer(qq, states[qq.id]);
        return `Q: ${qq.prompt}\nA: ${a}`;
      });
      setSubmitted(true);
      sendLandingAgentQuery(parts.join('\n\n'), { ...followUpOf(turn), answers: turn?.sessionId ?? null });
    } else {
      setPageIdx((p) => Math.min(questions.length - 1, p + 1));
    }
  };

  const handleSubmit = React.useCallback(() => {
    if (!isActive || !allAnswered) return;
    const route = routeOf(questions, states[questions[0]?.id]?.selectedIndex ?? null);
    if (route && turn?.askedRequest) {
      setSubmitted(true);
      sendLandingAgentQuery(turn.askedRequest.query, { reask: turn.askedRequest, route });
      return;
    }
    const earlier = earlierOf(questions, states[questions[0]?.id]?.selectedIndex ?? null);
    if (earlier && turn?.askedRequest) {
      setSubmitted(true);
      sendLandingAgentQuery(turn.askedRequest.query, { reask: turn.askedRequest, earlier });
      return;
    }
    const scope = scopeOf(questions, states[questions[0]?.id]?.selectedIndex ?? null);
    if (scope && turn?.askedRequest) {
      setSubmitted(true);
      reaskInPerspective(turn, scope.perspective_id, scope.for);
      return;
    }
    const parts = questions.map((q) => {
      const a = formatAnswer(q, states[q.id]);
      return `Q: ${q.prompt}\nA: ${a}`;
    });
    setSubmitted(true);
    sendLandingAgentQuery(parts.join('\n\n'), { ...followUpOf(turn), answers: turn?.sessionId ?? null });
  }, [isActive, allAnswered, questions, states, sendLandingAgentQuery, reaskInPerspective, turn]);

  const handleSkip = React.useCallback(() => {
    if (!isActive) return;
    setSubmitted(true);
    sendLandingAgentQuery('Use your best judgment — pick sensible defaults for the questions above and proceed.', { ...followUpOf(turn), answers: turn?.sessionId ?? null });
  }, [isActive, sendLandingAgentQuery, turn]);

  // Keyboard handling at card level
  React.useEffect(() => {
    if (!isActive) return;
    const el = cardRef.current;
    if (!el) return;
    const handler = (e: KeyboardEvent) => {
      // Don't hijack typing inside the custom text input
      const tgt = e.target as HTMLElement | null;
      const inInput = !!tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA');
      const qid = focusedQid;
      const q = qid ? questions.find((x) => x.id === qid) : null;
      const opts = q?.options || [];
      const rowCount = opts.length + 1; // +1 for "Something else"

      if (e.key === 'Escape') {
        e.preventDefault();
        handleSkip();
        return;
      }
      // Enter on a focused control is that control's own click
      if (e.key === 'Enter' && tgt?.closest('button')) return;
      if (e.key === 'Enter' && !inInput) {
        e.preventDefault();
        if (!currentAnswered) return;
        if (isLastPage) handleSubmit();
        else setPageIdx((p) => Math.min(questions.length - 1, p + 1));
        return;
      }
      if (!q) return;
      if (e.key === 'ArrowDown' && !inInput) {
        e.preventDefault();
        const cur = states[qid!]?.selectedIndex ?? -1;
        pickOption(qid!, Math.min(rowCount - 1, cur + 1));
      } else if (e.key === 'ArrowUp' && !inInput) {
        e.preventDefault();
        const cur = states[qid!]?.selectedIndex ?? rowCount;
        pickOption(qid!, Math.max(0, cur - 1));
      } else if (e.key === 'ArrowLeft' && !inInput) {
        e.preventDefault();
        setPageIdx((p) => Math.max(0, p - 1));
      } else if (e.key === 'ArrowRight' && !inInput) {
        if (!currentAnswered) return;
        e.preventDefault();
        setPageIdx((p) => Math.min(questions.length - 1, p + 1));
      } else if (!inInput && /^[1-9]$/.test(e.key)) {
        const n = parseInt(e.key, 10) - 1;
        if (n < rowCount) {
          e.preventDefault();
          pickOption(qid!, n, true);
        }
      }
    };
    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
  }, [isActive, focusedQid, questions, states, handleSubmit, handleSkip, pageIdx, isLastPage, currentAnswered]);

  // Auto-focus the card so keyboard shortcuts work immediately
  React.useEffect(() => {
    if (isActive) cardRef.current?.focus();
  }, [isActive]);

  if (!clar) return null;

  // ---- Collapsed / answered state ----
  if (!isActive) {
    return (
      <Box>
        {questions.map((q) => {
          const answer = formatAnswer(q, states[q.id] || { selectedIndex: null, customText: '' });
          return (
            <Box key={q.id} sx={{ '&:not(:last-child)': { mb: 1 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {q.prompt}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {answer || '—'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  }

  // ---- Active state ----
  return (
    <Box
      ref={cardRef}
      tabIndex={0}
      sx={{ borderRadius: '8px', ...stromaFocusRing }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, gap: 1 }}>
        <Typography variant="body1" sx={QUESTION}>
          {currentQ?.prompt}
        </Typography>
        {questions.length > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setPageIdx((p) => Math.max(0, p - 1))}
              disabled={pageIdx === 0}
              aria-label="Previous question"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 44, textAlign: 'center' }}>
              {pageIdx + 1} of {questions.length}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setPageIdx((p) => Math.min(questions.length - 1, p + 1))}
              disabled={isLastPage || !currentAnswered}
              aria-label="Next question"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <IconButton size="small" onClick={() => { if (turn) resolveWait(turn.id); resetLandingAgent(); }} aria-label="Cancel">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {currentQ && (() => {
        const q = currentQ;
        const st = states[q.id] || { selectedIndex: null, customText: '' };
        const opts = q.options || [];
        const customIdx = opts.length;
        const isCustom = st.selectedIndex === customIdx;
        return (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {opts.map((opt, i) => {
                const selected = st.selectedIndex === i;
                return (
                  <ButtonBase
                    key={opt}
                    aria-pressed={selected}
                    onClick={() => pickOption(q.id, i, true)}
                    sx={optionRow(selected)}
                  >
                    <Box component="span" sx={{ width: 16, flexShrink: 0, textAlign: 'center', fontSize: 12.5, color: 'var(--ink-3)', fontVariantNumeric: 'tabular-nums' }}>
                      {i + 1}
                    </Box>
                    <Typography variant="body2" component="span" sx={{ flex: 1, minWidth: 0, color: 'text.primary', overflowWrap: 'anywhere' }}>
                      {opt}
                    </Typography>
                    {selected && <KeyboardReturnIcon sx={{ fontSize: 15, color: 'var(--ink-3)', '@media (hover: none)': { display: 'none' } }} />}
                  </ButtonBase>
                );
              })}

              {/* Something else row: a line that opens the input in its place */}
              <Box
                component={isCustom ? 'div' : ButtonBase}
                {...(isCustom ? {} : { onClick: () => pickOption(q.id, customIdx) })}
                sx={optionRow(isCustom)}
              >
                <EditIcon sx={{ fontSize: 15, width: 16, flexShrink: 0, color: 'var(--ink-3)' }} />
                {isCustom ? (
                  <TextField
                    inputRef={customInputRef}
                    size="small"
                    variant="standard"
                    fullWidth
                    placeholder="Type your answer…"
                    value={st.customText}
                    onChange={(e) => setQuestion(q.id, { customText: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!currentAnswered) return;
                        if (isLastPage) handleSubmit();
                        else setPageIdx((p) => Math.min(questions.length - 1, p + 1));
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    InputProps={{ disableUnderline: true, sx: { fontSize: 14 } }}
                  />
                ) : (
                  <Typography variant="body2" component="span" sx={{ flex: 1, color: 'text.secondary' }}>
                    Something else
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      })()}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {isLastPage ? (
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ borderColor: 'var(--line)' }}
            disabled={!allAnswered}
            onClick={handleSubmit}
          >
            Submit answers
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ borderColor: 'var(--line)' }}
            disabled={!currentAnswered}
            onClick={() => setPageIdx((p) => Math.min(questions.length - 1, p + 1))}
          >
            Next
          </Button>
        )}
        <Button variant="text" size="small" onClick={handleSkip} sx={{ color: 'text.secondary' }}>
          Skip
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: 'var(--ink-3)', display: { xs: 'none', sm: 'block' } }}>
          {questions.length > 1
            ? '1–9 pick · ↑↓ options · ←→ questions · Enter next · Esc skip'
            : '1–9 pick · ↑↓ options · Enter submit · Esc skip'}
        </Typography>
      </Box>
    </Box>
  );
}
